import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  Crown,
  Zap,
  Users,
  ShoppingCart,
  CalendarDays,
  Check,
  Loader2,
  AlertCircle,
  CreditCard
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const MerchantSubscriptionManage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [featureAccess, setFeatureAccess] = useState({ base: false, crm: false, pos: false, services: false });
  const [v2Pricing, setV2Pricing] = useState(null);
  const [processingUpgrade, setProcessingUpgrade] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [subRes, pricingRes] = await Promise.all([
        fetch(`${API}/merchant/subscription`, { headers, credentials: "include" }),
        fetch(`${API}/pricing/v2`, { credentials: "include" })
      ]);

      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData.subscription);
        if (subData.feature_access) {
          setFeatureAccess(subData.feature_access);
        }
      }
      if (pricingRes.ok) {
        setV2Pricing(await pricingRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeAddon = async (addonId) => {
    setProcessingUpgrade(true);
    try {
      // Create a new checkout session for adding the addon
      const currentAddons = subscription?.addons || [];
      const newAddons = [...currentAddons, addonId];
      
      const res = await fetch(`${API}/stripe/v2/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          addons: newAddons,
          success_url: `${window.location.origin}/merchant/subscription-manage?upgraded=true`,
          cancel_url: `${window.location.origin}/merchant/subscription-manage?canceled=true`
        })
      });

      const data = await res.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || "Failed to create checkout session");
      }
    } catch (error) {
      toast.error(error.message || "Failed to process upgrade. Please try again.");
      setProcessingUpgrade(false);
    }
  };

  const calculateCurrentTotal = () => {
    if (!v2Pricing || !featureAccess?.base) return 0;
    let total = v2Pricing.base_plan?.price || 99;
    if (featureAccess.crm) total += v2Pricing.addons?.crm?.price || 79;
    if (featureAccess.pos) total += v2Pricing.addons?.pos?.price || 149;
    if (featureAccess.services) total += v2Pricing.addons?.services?.price || 99;
    return total;
  };

  const addonConfig = [
    {
      id: "crm",
      name: "CRM Add-on",
      description: "Customer relationship management, email marketing, and loyalty programs",
      icon: Users,
      features: ["Customer database", "Email campaigns", "Loyalty tracking", "Marketing automation"]
    },
    {
      id: "pos",
      name: "POS Add-on",
      description: "Full point-of-sale system with inventory management",
      icon: ShoppingCart,
      features: ["POS Terminal", "Inventory tracking", "Sales reports", "Receipt printing"]
    },
    {
      id: "services",
      name: "Services & Booking",
      description: "Online booking system and appointment scheduling",
      icon: CalendarDays,
      features: ["Online booking", "Calendar sync", "Appointment reminders", "Service management"]
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/merchant" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </Link>
              <img src={COMPANY_LOGO} alt="V" className="h-8" />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <CreditCard className="h-4 w-4" />
              <span>Subscription Management</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Current Plan Summary */}
        {featureAccess?.base ? (
          <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="h-6 w-6 text-amber-300" />
                  <span className="text-lg font-semibold">Your Current Plan</span>
                </div>
                <p className="text-white/80 text-sm">
                  5% Commission Plan
                  {featureAccess.crm && " + CRM"}
                  {featureAccess.pos && " + POS"}
                  {featureAccess.services && " + Services"}
                </p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold">5%</span>
                <span className="text-white/80"> commission</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-200">No Active Subscription</h3>
                <p className="text-sm text-amber-600 dark:text-amber-400">Complete your onboarding to subscribe to a plan.</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/merchant/go-live")}
              className="mt-4 bg-amber-600 hover:bg-amber-700"
            >
              Complete Onboarding
            </Button>
          </div>
        )}

        {/* Base Plan */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Base Plan</h2>
          <div className={`p-6 rounded-xl border-2 ${
            featureAccess?.base 
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" 
              : "border-gray-200 dark:border-gray-700"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  featureAccess?.base 
                    ? "bg-emerald-100 dark:bg-emerald-900/50" 
                    : "bg-gray-100 dark:bg-gray-800"
                }`}>
                  <Zap className={`h-6 w-6 ${featureAccess?.base ? "text-emerald-600" : "text-gray-400"}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">5% Commission Plan</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Core platform access and all features included</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-slate-900 dark:text-white">5%</span>
                <span className="text-gray-500 dark:text-gray-400"> commission</span>
                {featureAccess?.base && (
                  <div className="flex items-center gap-1 mt-1 text-emerald-600 dark:text-emerald-400">
                    <Check className="h-4 w-4" />
                    <span className="text-sm">Active</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add-ons */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Add-ons</h2>
          <div className="grid gap-4">
            {addonConfig.map((addon) => {
              const isActive = featureAccess?.[addon.id] === true;
              const price = v2Pricing?.addons?.[addon.id]?.price || 0;
              
              return (
                <div
                  key={addon.id}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    isActive
                      ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700"
                  }`}
                  data-testid={`addon-card-${addon.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isActive
                          ? "bg-indigo-100 dark:bg-indigo-900/50"
                          : "bg-gray-100 dark:bg-gray-800"
                      }`}>
                        <addon.icon className={`h-6 w-6 ${isActive ? "text-indigo-600" : "text-gray-400"}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{addon.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{addon.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {addon.features.map((feature, idx) => (
                            <span
                              key={idx}
                              className={`text-xs px-2 py-1 rounded-full ${
                                isActive
                                  ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                              }`}
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="mb-2">
                        <span className="text-2xl font-bold text-slate-900 dark:text-white">£{price}</span>
                        <span className="text-gray-500 dark:text-gray-400">/mo</span>
                      </div>
                      {isActive ? (
                        <div className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
                          <Check className="h-4 w-4" />
                          <span className="text-sm font-medium">Active</span>
                        </div>
                      ) : featureAccess?.base ? (
                        <Button
                          onClick={() => handleUpgradeAddon(addon.id)}
                          disabled={processingUpgrade}
                          className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700"
                          data-testid={`upgrade-${addon.id}-btn`}
                        >
                          {processingUpgrade ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Zap className="h-4 w-4 mr-1" />
                              Add
                            </>
                          )}
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-400">Subscribe first</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Need to cancel or modify your subscription?
          </p>
          <a
            href="mailto:support@veyronix.co.uk"
            className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
          >
            Contact Support
          </a>
        </div>
      </main>
    </div>
  );
};

export default MerchantSubscriptionManage;
